import React, { useState } from 'react';
import SVGBuildingCell from './components/SVGBuildingCell';
import CompactPlayableCell from './components/CompactPlayableCell';
import CompactStatsPanel from './components/CompactStatsPanel';
import { ValidCell } from './types';
import { BUILDINGS, GRID } from './constants';
import toast from 'react-hot-toast';

const DemoPageV2: React.FC = () => {
    const [selectedPosition, setSelectedPosition] = useState<ValidCell | null>(null);
    const [hoveredCell, setHoveredCell] = useState<ValidCell | null>(null);
    const [teamAPositions] = useState<ValidCell[]>(['M1', 'K2', 'D3', 'L4']);
    const [teamBPositions] = useState<ValidCell[]>(['M5', 'K4', 'D1', 'W2']);
    const [competitorPositions] = useState<ValidCell[]>(['M2']);

    // Mock round scores for history
    const roundScores = [
        { teamA: 28, teamB: 15 },
        { teamA: 42, teamB: 38 },
        { teamA: 38, teamB: 41 }
    ];

    const handleCellClick = (position: ValidCell) => {
        setSelectedPosition(position);
        toast.success(`Position ${position} sélectionnée !`, { icon: '🎯' });
    };

    const handleConfirm = () => {
        if (selectedPosition) {
            toast.success(`${selectedPosition} confirmée ! +42 pts estimés`, { icon: '✅' });
            setSelectedPosition(null);
        }
    };

    // Calculate building control for each building
    const getBuildingControl = (buildingId: string) => {
        const building = BUILDINGS[buildingId as keyof typeof BUILDINGS];
        if (!building) return { teamA: 0, teamB: 0, competitor: 0 };

        let controlA = 0, controlB = 0, controlC = 0;
        const adjacentCells = building.adjacent as readonly string[];

        adjacentCells.forEach((pos) => {
            const cell = pos as ValidCell;
            if (teamAPositions.includes(cell)) controlA++;
            if (teamBPositions.includes(cell)) controlB++;
            if (competitorPositions.includes(cell)) controlC++;
        });

        const total = adjacentCells.length;
        return {
            teamA: controlA / total,
            teamB: controlB / total,
            competitor: controlC / total
        };
    };

    const isPlayableCell = (cell: string): boolean => {
        return cell !== '' && cell.match(/^[MKDLW][1-5]$/) !== null;
    };

    const getCellState = (cell: ValidCell): 'normal' | 'hover' | 'selected' | 'occupied' => {
        if (teamAPositions.includes(cell) || teamBPositions.includes(cell)) return 'occupied';
        if (selectedPosition === cell) return 'selected';
        if (hoveredCell === cell) return 'hover';
        return 'normal';
    };

    const getCellTeam = (cell: ValidCell): 'A' | 'B' | undefined => {
        if (teamAPositions.includes(cell)) return 'A';
        if (teamBPositions.includes(cell)) return 'B';
        return selectedPosition === cell ? 'A' : undefined;
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--bg-primary)',
            padding: '16px',
            color: 'var(--text-primary)',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Compact Header */}
            <div style={{
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h1 style={{
                    fontSize: '1.75rem',
                    fontWeight: '800',
                    margin: 0,
                    background: 'linear-gradient(135deg, var(--team-a), #60A5FA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    🎮 DÉMO V2 - Interface Raffinée
                </h1>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    ✨ SVG Colorisés | Labels Toit | Effets 3D | Sans Scroll
                </div>
            </div>

            {/* Main Layout - Grid + Stats Side by Side */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '560px 420px',
                gap: '20px',
                maxWidth: '1000px',
                margin: '0 auto'
            }}>
                {/* Left: Compact Grid */}
                <div>
                    <div className="glass-card" style={{ padding: '16px' }}>
                        <h3 style={{
                            fontSize: '1rem',
                            fontWeight: '700',
                            marginBottom: '12px',
                            margin: 0
                        }}>
                            🏢 Grille Interactive Compacte
                        </h3>

                        {/* Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(9, 1fr)',
                            gap: '3px',
                            marginTop: '12px',
                            padding: '12px',
                            background: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            justifyItems: 'center',
                            alignItems: 'center'
                        }}>
                            {GRID.map((row, rowIndex) => (
                                row.map((cell, colIndex) => {
                                    const key = `${rowIndex}-${colIndex}`;

                                    // Building cell
                                    if (cell && !isPlayableCell(cell)) {
                                        const building = BUILDINGS[cell as keyof typeof BUILDINGS];
                                        if (building) {
                                            return (
                                                <SVGBuildingCell
                                                    key={key}
                                                    buildingId={cell}
                                                    baseScore={building.score}
                                                    controlRatio={getBuildingControl(cell)}
                                                    isHovered={hoveredCell === cell as any}
                                                />
                                            );
                                        }
                                    }

                                    // Playable cell
                                    if (isPlayableCell(cell)) {
                                        const validCell = cell as ValidCell;
                                        const isBlocked = competitorPositions.includes(validCell);

                                        if (isBlocked) {
                                            return (
                                                <div
                                                    key={key}
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        background: 'linear-gradient(45deg, rgba(245, 158, 11, 0.3) 25%, transparent 25%, transparent 75%, rgba(245, 158, 11, 0.3) 75%)',
                                                        backgroundSize: '8px 8px',
                                                        border: '2px solid var(--competitor)',
                                                        borderRadius: 'var(--radius-sm)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '0.7rem',
                                                        color: 'var(--competitor)',
                                                        fontWeight: '700'
                                                    }}
                                                >
                                                    🚫
                                                </div>
                                            );
                                        }

                                        return (
                                            <CompactPlayableCell
                                                key={key}
                                                position={validCell}
                                                state={getCellState(validCell)}
                                                team={getCellTeam(validCell)}
                                                onClick={() => handleCellClick(validCell)}
                                                onHover={(hovered) => setHoveredCell(hovered ? validCell : null)}
                                                showSelectionOrb={true}
                                            />
                                        );
                                    }

                                    // Empty cell
                                    return <div key={key} style={{ width: '50px', height: '50px' }} />;
                                })
                            ))}
                        </div>
                    </div>

                    {/* Action Panel */}
                    <div className="glass-card" style={{
                        padding: '16px',
                        marginTop: '16px'
                    }}>
                        <h3 style={{
                            fontSize: '1rem',
                            fontWeight: '700',
                            marginBottom: '12px',
                            margin: 0
                        }}>
                            🎯 Votre Sélection
                        </h3>
                        {selectedPosition ? (
                            <div>
                                <div style={{
                                    padding: '16px',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '2px solid var(--team-a)',
                                    marginBottom: '12px',
                                    marginTop: '12px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔵</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                                        {selectedPosition}
                                    </div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '4px' }}>
                                        Position sélectionnée
                                    </div>
                                </div>
                                <button
                                    className="btn btn-success"
                                    onClick={handleConfirm}
                                    style={{ width: '100%' }}
                                >
                                    ✓ Confirmer
                                </button>
                            </div>
                        ) : (
                            <div style={{
                                padding: '24px',
                                textAlign: 'center',
                                color: 'var(--text-muted)',
                                marginTop: '12px'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👆</div>
                                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                    Cliquez sur une cellule
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Compact Stats */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <CompactStatsPanel
                        teamAScore={142}
                        teamBScore={128}
                        teamAAlignments={1}
                        teamBAlignments={0}
                        currentRound={3}
                        maxRounds={12}
                        roundScores={roundScores}
                    />

                    {/* Legend */}
                    <div className="glass-card" style={{ padding: '16px' }}>
                        <h4 style={{
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            marginBottom: '12px',
                            margin: 0
                        }}>
                            📖 Légende
                        </h4>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            fontSize: '0.75rem',
                            marginTop: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'var(--team-a)',
                                    borderRadius: '4px'
                                }} />
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    Bâtiment contrôlé par <strong style={{ color: 'var(--team-a)' }}>Team A</strong>
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'var(--team-b)',
                                    borderRadius: '4px'
                                }} />
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    Bâtiment contrôlé par <strong style={{ color: 'var(--team-b)' }}>Team B</strong>
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'rgba(147, 51, 234, 0.5)',
                                    borderRadius: '4px'
                                }} />
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    Bâtiment <strong style={{ color: 'rgb(147, 51, 234)' }}>contesté</strong>
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'var(--competitor)',
                                    borderRadius: '4px'
                                }} />
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--competitor)' }}>Concurrent</strong> présent
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Features */}
                    <div className="glass-card" style={{ padding: '16px' }}>
                        <h4 style={{
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            marginBottom: '12px',
                            margin: 0
                        }}>
                            ✨ Nouveautés V2
                        </h4>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            marginTop: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            fontSize: '0.75rem'
                        }}>
                            <li style={{ color: 'var(--success)' }}>✓ Bâtiments SVG colorisés intelligemment</li>
                            <li style={{ color: 'var(--success)' }}>✓ Positions avec label sur le toit</li>
                            <li style={{ color: 'var(--success)' }}>✓ Grille compacte ~560×560px</li>
                            <li style={{ color: 'var(--success)' }}>✓ Stats condensées, pas de scroll</li>
                            <li style={{ color: 'var(--success)' }}>✓ Orbes de sélection 🔵/🔴</li>
                            <li style={{ color: 'var(--success)' }}>✓ Modal historique (clic 📈)</li>
                            <li style={{ color: 'var(--success)' }}>✓ Effets 3D conservés</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DemoPageV2;
